<script>
  import { tick, untrack } from 'svelte';
  import {
    billing,
    closeSubscriptionModal,
    subscribe,
    changePlan,
    manageSubscription,
    resetSubscriptionCheckout,
    loadPlans,
    refreshTrialEligibility,
    getCurrentSubscriptionCheckoutUrl
  } from '../state/billing.svelte.js';
  import { auth } from '../state/auth.svelte.js';
  import { getSubscriptionViewModel, getSubscriptionTierLabel, formatSubscriptionDate } from '../utils/subscription-ui.js';
  import {
    BILLING_LINKS,
    buildBillingLegalConsent,
    buildTrialConsent,
    formatPlanPrice,
    getPlanAction,
    getPlanPresentation,
    getTrialTerms
  } from '../utils/subscription-purchase.js';
  import { focusDialog } from '../utils/dialog-focus.js';

  let term = $state(auth.subscription?.term === 'annual' ? 'annual' : 'monthly');
  let submittingPlan = $state(null);
  let choice = $state(null);
  let termsAccepted = $state(false);
  let reachedEnd = $state(false);
  let termsElement = $state(null);
  let confirmationHeading = $state(null);
  let dialogElement = $state(null);
  let lastUser = auth.username;

  const subscriptionView = $derived(getSubscriptionViewModel(auth.subscription, { isUnlimited: auth.isUnlimited }));
  const visiblePlans = $derived((billing.plans || [])
    .filter((plan) => plan.term === term)
    .sort((a, b) => a.priceUsd - b.priceUsd)
    .map((plan) => getPlanPresentation(plan, billing.plans, billing.planContent)));
  const trialTerms = $derived(choice ? getTrialTerms(choice.plan, billing.planContent) : []);
  const busy = $derived(submittingPlan !== null);
  const checkoutUrl = $derived(getCurrentSubscriptionCheckoutUrl());

  $effect(() => {
    const username = auth.username;
    if (username !== lastUser) {
      lastUser = username;
      choice = null;
      untrack(() => {
        resetSubscriptionCheckout();
        loadPlans();
      });
    }
  });

  $effect(() => {
    if (confirmationHeading) confirmationHeading.focus();
  });

  $effect(() => {
    if (!termsElement) return;
    updateReadProgress();
    const observer = new ResizeObserver(updateReadProgress);
    observer.observe(termsElement);
    return () => observer.disconnect();
  });

  function updateReadProgress() {
    if (termsElement) reachedEnd = termsElement.scrollHeight - termsElement.clientHeight <= termsElement.scrollTop + 2;
  }

  function close() {
    if (!busy) closeSubscriptionModal();
  }

  function reviewSubscription(plan, trial = false) {
    billing.subscriptionError = null;
    termsAccepted = false;
    reachedEnd = false;
    choice = { plan, trial };
  }

  async function returnToPlans() {
    if (busy) return;
    choice = null;
    await tick();
    dialogElement?.querySelector('button')?.focus();
  }

  async function handleConfirm() {
    if (!choice || !termsAccepted || (choice.trial && !reachedEnd)) return;
    const selected = choice;
    submittingPlan = `${selected.plan.planId}:${selected.trial ? 'trial' : 'paid'}`;
    try {
      const result = await subscribe(selected.plan.planId, selected.plan.term, {
        startTrial: selected.trial,
        trialConsent: buildTrialConsent(selected.plan, selected.trial ? 'accepted_trial' : 'declined_trial')
      });
      if (result) {
        choice = null;
        await tick();
        dialogElement?.querySelector('button')?.focus();
      }
    } finally {
      submittingPlan = null;
    }
  }

  async function handleChange(plan) {
    submittingPlan = `${plan.planId}:change`;
    try {
      await changePlan(plan.planId, plan.term, buildBillingLegalConsent());
    } finally {
      submittingPlan = null;
    }
  }

  async function handleManage() {
    if (subscriptionView.management.kind === 'none') return;
    submittingPlan = 'manage';
    try {
      await manageSubscription(auth.subscription);
    } finally {
      submittingPlan = null;
    }
  }

  function handlePlan(plan) {
    const action = getPlanAction(auth.subscription, plan, { isUnlimited: auth.isUnlimited });
    if (action.kind === 'change') return handleChange(plan);
    if (action.kind === 'manage') return handleManage();
    if (action.kind === 'subscribe') reviewSubscription(plan);
  }
</script>

<div
  class="overlay"
  role="presentation"
  onclick={(event) => { if (event.target === event.currentTarget) close(); }}
>
  <div
    class="dialog"
    role="dialog"
    aria-modal="true"
    aria-labelledby={choice ? 'confirm-title' : 'sub-title'}
    tabindex="-1"
    bind:this={dialogElement}
    use:focusDialog={{ onClose: close }}
  >
    <div class="header">
      <div>
        <h3 id="sub-title">Sogni plans</h3>
        <p class="sub">Eligible Supernet models are included. Partner models use Premium Spark.</p>
      </div>
      <button class="close-btn" onclick={close} disabled={busy} aria-label="Close plans">×</button>
    </div>

    <div class="content" aria-busy={busy || billing.plansLoading}>
      {#if choice}
        <section class="confirmation" aria-labelledby="confirm-title">
          <h4 id="confirm-title" tabindex="-1" bind:this={confirmationHeading}>
            {choice.trial ? 'Free trial terms' : 'Review paid subscription'}
          </h4>
          <p class="chosen-plan">{choice.plan.name} · {choice.plan.term === 'annual' ? 'Annual' : 'Monthly'} · {choice.plan.charge}</p>
          {#if choice.trial}
            <div class="hold-notice">
              <strong>If your trial is approved: {formatPlanPrice(choice.plan.priceUsd)} temporary card hold · $0 collected</strong>
              <p>For an approved free trial, your bank may show <strong>SOGNI* NO CHARGE</strong> as a pending charge. Sogni never collects this verification amount and requests its release immediately. Your bank may take a few business days to remove it.</p>
            </div>
            <p>If approved, your {choice.plan.content.trialDays}-day trial starts after your card is verified. Unless you cancel before it ends, billing starts automatically at <strong>{choice.plan.charge}</strong>.</p>
            <p><strong>Continue only if Stripe confirms the free trial and $0 due now.</strong> Eligibility can change before checkout. If Stripe shows payment due now, cancel and return here to review a paid subscription separately.</p>
            <!-- The scrollable terms need keyboard focus so users can read to the end. -->
            <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
            <div class="terms-scroll" role="region" aria-label="Free trial terms" tabindex="0" bind:this={termsElement} onscroll={updateReadProgress}>
              {#each trialTerms as item (item.title)}
                <section class="term">
                  <h5>{item.title}</h5>
                  <p>{item.body}</p>
                </section>
              {/each}
            </div>
          {:else}
            <p><strong>{formatPlanPrice(choice.plan.priceUsd)} is due now.</strong> This choice skips any free trial and starts paid limits. Your subscription renews automatically at {choice.plan.charge} until you cancel renewal.</p>
            {#if billing.trialEligible}
              <p class="muted">You are eligible for a free trial. Return to the plans to choose it instead.</p>
            {:else}
              <p class="muted">A free trial is not available for this account.</p>
            {/if}
          {/if}
          {#if choice.plan.term === 'annual'}
            <p class="policy">{billing.planContent?.footnotes?.annualCommitment}</p>
          {/if}
          <p class="policy">
            Review the <a href={BILLING_LINKS.terms} target="_blank" rel="noreferrer">Terms of Service</a>,
            <a href={BILLING_LINKS.refunds} target="_blank" rel="noreferrer">Refund & Generation Policy</a>, and
            <a href={BILLING_LINKS.privacy} target="_blank" rel="noreferrer">Privacy Policy</a>.
          </p>
          <label class="consent-check">
            <input type="checkbox" bind:checked={termsAccepted} disabled={busy} />
            <span>{choice.trial
              ? `I authorize the temporary ${formatPlanPrice(choice.plan.priceUsd)} card hold if my free trial is approved, understand Sogni will not collect that hold, and accept the trial and subscription terms. I will continue only if Stripe confirms the free trial and $0 due now.`
              : `I agree to pay ${formatPlanPrice(choice.plan.priceUsd)} now, accept the subscription terms, and acknowledge the Refund & Generation Policy.`}</span>
          </label>
          {#if choice.trial && !reachedEnd}
            <p class="hint" role="status">Read to the end of the trial terms to continue.</p>
          {/if}
          <button class="btn-primary" onclick={handleConfirm} disabled={busy || !termsAccepted || (choice.trial && !reachedEnd)}>
            {busy ? 'Opening secure checkout…' : choice.trial ? 'Accept & continue to Stripe' : `Continue to Stripe · ${formatPlanPrice(choice.plan.priceUsd)} due now`}
          </button>
          <button class="btn-ghost" onclick={returnToPlans} disabled={busy}>Back to plans</button>
        </section>
      {:else}
        {#if subscriptionView.showCurrent}
          <section class="current" aria-label="Current subscription">
            <span class="badge" class:info={subscriptionView.badgeTone === 'info'} class:warning={subscriptionView.badgeTone === 'warning'} class:error-tone={subscriptionView.badgeTone === 'error'}>{subscriptionView.badgeLabel}</span>
            <p>{subscriptionView.description}</p>
            {#if auth.subscription?.scheduledTier || auth.subscription?.scheduledTerm}
              <p class="policy">Scheduled change: {getSubscriptionTierLabel(auth.subscription.scheduledTier || auth.subscription.tier)} · {auth.subscription.scheduledTerm || auth.subscription.term}{auth.subscription.scheduledChangeAt ? ` on ${formatSubscriptionDate(auth.subscription.scheduledChangeAt)}` : ' at renewal'}.</p>
            {/if}
            {#if subscriptionView.managementLabel}
              <button class="btn-ghost" onclick={handleManage} disabled={busy}>{submittingPlan === 'manage' ? 'Opening…' : subscriptionView.managementLabel}</button>
            {:else if subscriptionView.managementNote}
              <p class="management-note">{subscriptionView.managementNote}</p>
            {/if}
          </section>
        {/if}

        <div class="term-toggle" role="group" aria-label="Billing term">
          <button aria-pressed={term === 'monthly'} class:active={term === 'monthly'} onclick={() => (term = 'monthly')} disabled={busy}>Monthly</button>
          <button aria-pressed={term === 'annual'} class:active={term === 'annual'} onclick={() => (term = 'annual')} disabled={busy}>Annual</button>
        </div>

        {#if billing.plansLoading}
          <div class="center" role="status"><span class="spinner"></span> Loading plans…</div>
        {:else if billing.plansError}
          <div class="error" role="alert">{billing.plansError}</div>
          <button class="btn-ghost" onclick={loadPlans}>Retry loading plans</button>
        {:else if visiblePlans.length === 0}
          <div class="center muted">No plans available right now.</div>
        {:else}
          {#if !subscriptionView.showCurrent && billing.trialEligibility === 'error'}
            <div class="error" role="alert">{billing.trialError}</div>
            <button class="btn-ghost" onclick={refreshTrialEligibility}>Retry trial eligibility</button>
          {/if}
          <div class="plans">
            {#each visiblePlans as plan (plan.planId + plan.term)}
              {@const action = getPlanAction(auth.subscription, plan, { isUnlimited: auth.isUnlimited })}
              {@const eligibilityPending = action.kind === 'subscribe' && (billing.trialEligibility === 'loading' || billing.trialEligibility === 'error')}
              <section class="plan" class:featured={plan.planId === 'unlimited_pro'} aria-label={plan.name}>
                <h4 class="plan-name">{plan.name}</h4>
                <p class="tagline">{plan.content?.tagline}</p>
                <div class="plan-price"><span class="amount">{formatPlanPrice(plan.priceUsd)}</span><span class="per">/{plan.term === 'annual' ? 'year' : 'month'}</span></div>
                {#if plan.term === 'annual'}
                  <p class="plan-perk">{formatPlanPrice(plan.monthlyEquivalent)}/month · billed annually</p>
                  {#if plan.annualSavings > 0}<p class="saving">Save {formatPlanPrice(plan.annualSavings)}/year ({plan.annualSavingsPercent}%)</p>{/if}
                {/if}
                <ul class="perks">{#each plan.content?.perks || [] as perk}<li>{perk}</li>{/each}</ul>
                <p class="plan-perk">{plan.vendorDiscountBps / 100}% member discount on eligible partner models; separately billed in Premium Spark.</p>
                <details>
                  <summary>All benefits & limits</summary>
                  {#each plan.detailGroups as group (group.group)}
                    <h5>{group.group}</h5>
                    <ul>{#each group.items as item}<li>{item}</li>{/each}</ul>
                  {/each}
                </details>
                <button class="btn-primary" onclick={() => handlePlan(plan)} disabled={busy || action.disabled || eligibilityPending}>
                  {submittingPlan === `${plan.planId}:change` ? 'Opening…' : eligibilityPending && billing.trialEligibility === 'loading' ? 'Checking eligibility…' : action.label}
                </button>
                {#if action.kind === 'subscribe' && billing.trialEligible && plan.content?.trialDays > 0}
                  <button class="btn-ghost" onclick={() => reviewSubscription(plan, true)} disabled={busy}>Review {plan.content.trialDays}-day free trial</button>
                {/if}
              </section>
            {/each}
          </div>
          {#if subscriptionView.showCurrent && subscriptionView.provider === 'stripe'}
            <p class="policy">Stripe shows the final amount, proration and timing for a plan change before you confirm it. By continuing, you accept the <a href={BILLING_LINKS.terms} target="_blank" rel="noreferrer">Terms of Service</a> and acknowledge the <a href={BILLING_LINKS.refunds} target="_blank" rel="noreferrer">Refund & Generation Policy</a>. <a href={BILLING_LINKS.privacy} target="_blank" rel="noreferrer">Privacy Policy</a>.</p>
          {/if}
          {#if term === 'annual'}<p class="policy">{billing.planContent?.footnotes?.annualCommitment}</p>{/if}
          <p class="policy">{billing.planContent?.footnotes?.fairUse} <a href={billing.planContent?.footnotes?.fairUsePolicyUrl} target="_blank" rel="noreferrer">Full fair-use terms</a>.</p>
        {/if}
      {/if}

      {#if billing.subscriptionError}<p class="error" role="alert">{billing.subscriptionError}</p>{/if}
      {#if billing.subscriptionNotice}<p class="status" role="status">{billing.subscriptionNotice}</p>{/if}
      {#if checkoutUrl}<a class="checkout-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer">Continue to secure billing ↗</a>{/if}
      <p class="footnote">{subscriptionView.footnote}</p>
    </div>
  </div>
</div>

<style>
  .overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1100; padding: 20px; }
  .dialog { background: var(--color-bg); border-radius: 16px; width: 100%; max-width: 720px; box-shadow: var(--shadow-lg); border: 2px solid var(--color-primary-border); max-height: 92dvh; overflow-y: auto; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px; border-bottom: 1px solid var(--color-border); gap: 12px; }
  .header h3 { margin: 0; color: var(--color-primary); font-size: 1.2rem; }
  .sub { margin: 4px 0 0; font-size: 0.85rem; color: var(--color-text-muted); }
  .close-btn { background: none; border: none; color: var(--color-text-muted); font-size: 1.5rem; cursor: pointer; line-height: 1; min-width: 44px; min-height: 44px; flex-shrink: 0; }
  .content { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
  .term-toggle { display: flex; gap: 4px; background: var(--color-surface); border-radius: 10px; padding: 4px; align-self: center; }
  .term-toggle button { border: none; background: none; min-height: 44px; padding: 8px 20px; border-radius: 8px; font-weight: 600; font-size: 0.875rem; color: var(--color-text-muted); cursor: pointer; }
  .term-toggle button.active { background: var(--color-bg); color: var(--color-primary); box-shadow: var(--shadow-sm); }
  .plans { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .plan { display: flex; flex-direction: column; gap: 12px; min-width: 0; padding: 20px 16px; border: 2px solid var(--color-border); border-radius: 12px; background: var(--color-surface); }
  .plan.featured { border-color: var(--color-primary); }
  .plan-name { margin: 0; font-size: 1.05rem; font-weight: 700; color: var(--color-text); }
  .tagline, .plan-perk { margin: 0; font-size: 0.8rem; color: var(--color-text-muted); }
  .plan-price { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px; }
  .amount { font-size: 1.6rem; font-weight: 800; color: var(--color-primary); }
  .per, .saving { font-size: 0.8rem; color: var(--color-text-muted); }
  .saving { margin: 0; font-weight: 700; color: var(--color-primary); }
  .perks { flex: 1; }
  .perks, details ul { margin: 0; padding-left: 18px; font-size: 0.8rem; line-height: 1.5; }
  details { font-size: 0.8rem; }
  summary { cursor: pointer; min-height: 44px; display: flex; align-items: center; font-weight: 700; }
  h5 { margin: 12px 0 4px; font-size: 0.8rem; }
  .current { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; padding: 12px; border-radius: 10px; background: var(--color-surface); }
  .current p { margin: 0; font-size: 0.9rem; color: var(--color-text-muted); }
  .badge { display: inline-block; padding: 4px 12px; border-radius: 999px; background: #ebfbee; color: #2b8a3e; font-weight: 700; font-size: 0.85rem; }
  .badge.info { background: #e7f5ff; color: #1971c2; }
  .badge.warning { background: #fff4e6; color: #d9480f; }
  .badge.error-tone { background: #fff5f5; color: #c92a2a; }
  .btn-primary { width: 100%; background: var(--color-primary); color: white; border: none; min-height: 44px; padding: 10px 16px; border-radius: 8px; font-weight: 600; cursor: pointer; }
  .btn-primary:hover:not(:disabled) { background: var(--color-primary-hover); }
  button:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-ghost { background: none; color: var(--color-primary); border: none; min-height: 44px; padding: 6px; font-size: 0.85rem; font-weight: 600; cursor: pointer; text-decoration: underline; }
  .current .btn-ghost { padding-left: 0; }
  .confirmation { display: flex; flex-direction: column; gap: 14px; font-size: 0.9rem; }
  .confirmation h4, .confirmation p { margin: 0; }
  .chosen-plan { font-weight: 700; }
  .hold-notice { padding: 14px; background: var(--color-surface); border: 1px solid var(--color-primary-border); border-radius: 10px; }
  .hold-notice p { margin-top: 8px; line-height: 1.5; }
  .terms-scroll { max-height: 32dvh; overflow-y: auto; padding: 4px 12px 12px; border: 1px solid var(--color-border); border-radius: 8px; overscroll-behavior: contain; }
  .term p { font-size: 0.82rem; line-height: 1.6; color: var(--color-text-muted); }
  .consent-check { display: flex; align-items: flex-start; gap: 10px; padding: 10px; cursor: pointer; line-height: 1.5; }
  .consent-check input { width: 20px; height: 20px; margin-top: 2px; flex-shrink: 0; accent-color: var(--color-primary); }
  .center { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 24px; }
  .muted, .policy, .hint, .footnote { color: var(--color-text-muted); }
  .policy, .hint { margin: 0; font-size: 0.78rem; line-height: 1.6; }
  .error { margin: 0; color: #c92a2a; background: #fff5f5; padding: 12px; border-radius: 8px; font-size: 0.85rem; }
  .status { margin: 0; padding: 12px; background: var(--color-surface); border-radius: 8px; font-size: 0.85rem; }
  .checkout-link, a { color: var(--color-primary); }
  .footnote { margin: 0; font-size: 0.75rem; text-align: center; }
  .dialog :global(:focus-visible) { outline: 2px solid var(--color-primary); outline-offset: 3px; }
  .spinner { width: 16px; height: 16px; border: 2px solid var(--color-border); border-top-color: var(--color-primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @media (max-width: 559px) { .overlay { padding: 10px; } .dialog { max-height: 96dvh; } .plans { grid-template-columns: 1fr; } .header, .content { padding: 16px; } }
</style>
